/**
 * Firebase Core Configuration Module
 *
 * This module initializes and exports all Firebase services used throughout the application.
 * Firebase serves as the complete backend infrastructure for the application, providing:
 * - Authentication (auth)
 * - Database (Firestore and Realtime Database)
 * - Storage (for files and media)
 * - Analytics and Performance monitoring
 *
 * All Firebase-related functionality in the application depends on the services
 * initialized and exported from this file, making it a critical part of the application
 * architecture.
 */

// Import the functions you need from the SDKs you need
import { initializeApp, getApps, getApp } from 'firebase/app'
import { getAuth } from 'firebase/auth'
import { getFirestore } from 'firebase/firestore'
import { getStorage } from 'firebase/storage'
import { getAnalytics } from 'firebase/analytics'
import { getPerformance } from 'firebase/performance'
import { getDatabase } from 'firebase/database'

// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

/**
 * Firebase Configuration Object
 *
 * Contains the necessary credentials and identifiers to connect to the
 * specific Firebase project. These values are loaded from environment variables
 * to enhance security and allow for different environments (dev, staging, prod)
 * without code changes.
 *
 * Services enabled for this application:
 * - Authentication: User login and registration
 * - Firestore: NoSQL document database for structured data
 * - Realtime Database: Real-time synchronization for chat and live features
 * - Storage: File storage for user uploads and application assets
 * - Analytics: User behavior tracking and app usage statistics
 * - Performance: Application performance monitoring
 *
 * @type {Object}
 */
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
}

// Function to check if Firebase config is valid
const isFirebaseConfigValid = () => {
  const isValid =
    firebaseConfig &&
    firebaseConfig.apiKey &&
    firebaseConfig.apiKey.length > 0 &&
    firebaseConfig.projectId &&
    firebaseConfig.projectId.length > 0

  if (!isValid) {
    console.error('Firebase configuration is invalid:', {
      hasApiKey: !!firebaseConfig?.apiKey,
      hasProjectId: !!firebaseConfig?.projectId,
      apiKeyLength: firebaseConfig?.apiKey?.length || 0,
      projectIdLength: firebaseConfig?.projectId?.length || 0,
    })
  }

  return isValid
}

/**
 * Initialize Firebase Application and Services
 *
 * Firebase initialization is only performed if a valid configuration is available.
 * If environment variables are missing, the services will be null but app won't crash.
 */
let app
let auth = null
let firestore = null
let storage = null
let analytics = null
let perf = null
let rtdb = null

if (isFirebaseConfigValid()) {
  try {
    /**
     * Initialize Firebase Application
     *
     * Check if Firebase app already exists to prevent duplicate initialization.
     * This creates the core Firebase application instance that all services connect to.
     * All Firebase services will use this app instance for configuration.
     */
    const existingApps = getApps()
    if (existingApps.length > 0) {
      app = getApp() // Use existing app
      console.log('Using existing Firebase app')
    } else {
      app = initializeApp(firebaseConfig) // Create new app
      console.log('Firebase app initialized successfully')
    }

    /**
     * Initialize Firebase Services
     *
     * Each service is initialized with the Firebase app instance and provides
     * specific functionality to the application.
     */
    auth = getAuth(app)
    console.log('Firebase Auth initialized')

    firestore = getFirestore(app)
    console.log('Firestore initialized')

    storage = getStorage(app)
    console.log('Firebase Storage initialized')

    try {
      analytics = getAnalytics(app)
      console.log('Firebase Analytics initialized')
    } catch (analyticsError) {
      console.warn('Firebase Analytics not available:', analyticsError.message)
    }

    try {
      perf = getPerformance(app)
      console.log('Firebase Performance initialized')
    } catch (perfError) {
      console.warn('Firebase Performance not available:', perfError.message)
    }

    try {
      rtdb = getDatabase(app)
      console.log('Firebase Realtime Database initialized')
    } catch (rtdbError) {
      console.warn(
        'Firebase Realtime Database not available:',
        rtdbError.message
      )
    }

    console.log('All Firebase services initialized successfully')
  } catch (error) {
    console.error('Error initializing Firebase:', error)
    // Reset all services to null on failure
    firestore = null
    auth = null
    storage = null
    rtdb = null
    analytics = null
    perf = null
    app = null
  }
} else {
  console.warn(
    'Firebase configuration is invalid or missing. Firebase services will not be available.'
  )
}

/**
 * Export initialized services for use throughout the application
 *
 * These exports provide direct access to Firebase services for any component
 * or utility that imports them. This centralized initialization ensures that
 * all components use the same Firebase service instances.
 */
export { auth, firestore, storage, analytics, perf, rtdb }

/**
 * Custom hook for accessing all Firebase services
 *
 * This hook provides a convenient way to access all Firebase services
 * in functional components. Instead of importing individual services,
 * components can use this hook to get access to all services at once.
 *
 * Usage example:
 * ```jsx
 * const { auth, firestore } = useFirebase();
 * // Use auth and firestore in component
 * ```
 *
 * @returns {Object} Object containing all Firebase service instances
 */
export const useFirebase = () => {
  return {
    auth,
    firestore,
    storage,
    analytics,
    perf,
    rtdb,
  }
}
